import React, { useState, useEffect, useRef } from 'react';
import { useStateValue } from './StateProvider';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';
import configData from './Config';
import moment from 'moment';
import ReactToPrint from 'react-to-print';
import html3pdf from 'html3pdf';
import jsPDF from 'jspdf';
import { registerArabicFont } from "./Utils/registerarabicfont.js";

import Select from './Components/select.js';
import Save from './Components/save.js';
import Send from './Components/send.js';
import Title from './Title';
import { numberToWords, parseAmount } from './Utils/textparse.js';
import { getPermission } from './Utils/permissions.js';

import './Transactions.css';

const receiptAccountTypes = [
    { id: 'clients', name: 'Clients' },
    { id: 'suppliers', name: 'Suppliers' },
    { id: 'assetaccounts', name: 'Asset Accounts' },
    { id: 'liabilityaccounts', name: 'Liability Accounts' },
    { id: 'equityaccounts', name: 'Equity Accounts' },
]

const paymentAccountTypes = [
    { id: 'expenseaccounts', name: 'Expense Accounts' },
    { id: 'assetaccounts', name: 'Asset Accounts' },
    { id: 'liabilityaccounts', name: 'Liability Accounts' },
    { id: 'equityaccounts', name: 'Equity Accounts' },
]

const paymentBeneficiaryAccountTypes = [
    { id: 'staff', name: 'Staff' },
    { id: 'suppliers', name: 'Suppliers' },
    { id: 'clients', name: 'Clients' },
]

function Transactions({ state, setState }) {
    const [{ project }, dispatch] = useStateValue();

    const [filteredtransactions, setFilteredTransactions] = useState([]);
    const [search, setSearch] = useState('');
    const [selectedtransactions, setSelectedtransactions] = useState([]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const filtered = project.transactions.filter((transaction) =>
            transaction.name.toLowerCase().includes(search.toLowerCase()) ||
            (project.clients.find(item => item.id === transaction.from)?.name.toLowerCase().includes(search.toLowerCase())) ||
            transaction.id.toLowerCase().includes(search.toLowerCase()) ||
            transaction.amount.includes(search)
        );
        setFilteredTransactions(filtered);
    }, [project, search]);

    const handleCloseModal = () => {
        setState(state => ({ ...state, modalopen: false, modalcontent: null, modaltype: null, modaltitle: null }));
    }

    const addReceiptButton = () => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <AddReceipt />, modaltype: 'large', modaltitle: 'Add Receipt' }));
    }

    const addPaymentButton = () => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <AddPayment />, modaltype: 'large', modaltitle: 'Add Payment' }));
    }

    const editPaymentButton = (transaction) => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <EditPayment transaction={transaction} />, modaltype: 'large', modaltitle: 'Edit Payment' }));
    }

    const editReceiptButton = (transaction) => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <EditReceipt transaction={transaction} />, modaltype: 'large', modaltitle: 'Edit Receipt' }));
    }

    const viewReceiptButton = (transaction) => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <ViewReceipt transaction={transaction} />, modaltype: 'large', modaltitle: 'View Receipt' }));
    }

    const viewPaymentButton = (transaction) => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <ViewPayment transaction={transaction} />, modaltype: 'large', modaltitle: 'View Payment' }));
    }

    const removeTransactionButton = (transaction) => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <RemoveTransaction transaction={transaction} />, modaltype: 'small', modaltitle: 'Remove Transaction' }));
    }

    const viewTransactions = (transactions) => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <ViewTransactions transactions={transactions} />, modaltype: 'large', modaltitle: 'View Transactions' }));
    }

    const saveButton = ({ templates, types, context }) => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <Save templates={templates} types={types} context={context} state={state} setState={setState} />, modaltype: 'small', modaltitle: 'Save' }));
    }

    const sendButton = ({ templates, contact, context }) => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <Send templates={templates} contact={contact} context={context} state={state} setState={setState} />, modaltype: 'small', modaltitle: 'Send' }));
    }

    const exportPage = () => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <ExportPage />, modaltype: 'small', modaltitle: 'Export' }));
    }

    const importPage = () => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <ImportPage />, modaltype: 'small', modaltitle: 'Import' }));
    }

    function ExportPage() {
        const [result, setResult] = useState('');

        useEffect(() => {
            if (!selectedtransactions.length) {
                setResult('No data selected for export');
                return;
            }

            const exportedData = JSON.stringify(
                project.transactions.filter(t => selectedtransactions.includes(t.id)),
                null,
                2
            );

            navigator.clipboard.writeText(exportedData)
                .then(() => setResult('Selected data copied to clipboard'))
                .catch(err => setResult('Error copying data to clipboard: ' + err));
        }, [selectedtransactions]);

        return (
            <div className='modal_body'>
                <div className='modal_printable'>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            {result}
                        </div>
                    </div>
                </div>
                <div className='modal_actions'>
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    function ImportPage() {
        const [importText, setImportText] = useState('');
        const [newEntries, setNewEntries] = useState([]);

        const [loading, setLoading] = useState(false);
        const [result, setResult] = useState('');

        useEffect(() => {
            if (!importText) {
                setResult('');
                setNewEntries([]);
                return;
            }

            let isJSON = false;
            let data = [];

            try {
                const jsonData = JSON.parse(importText);
                isJSON = true;
                data = jsonData;
            } catch {
                isJSON = false;
            }

            if (isJSON) {
                setResult('Invalid format');
            }

            if (!isJSON) {
                const requiredAttributes = ["Type", "Date", "Name", "From", "To", "Subaccount", "Supplier", "Amount"];
                let rows = importText.split('\n').map(row => row.split('\t'));
                const headers = rows.shift().map(header => header.trim());

                const missingHeaders = requiredAttributes.filter(attr => !headers.includes(attr));

                if (headers.length === 0 || headers.length !== requiredAttributes.length || missingHeaders.length > 0) {
                    setResult(`Invalid headers detected. Missing headers: ${missingHeaders.join(', ')}`);
                    return;
                }

                if (rows.length === 0) {
                    setResult('No data rows detected.');
                    return;
                }

                data = rows.map(row => {
                    let entry = {};
                    headers.forEach((header, index) => {
                        entry[header] = row[index]?.trim() || '';
                    });
                    return entry;
                });

                const convertedEntries = [];

                for (let i = 0; i < data.length; i++) {
                    const entry = data[i];
                    let convertedEntry = {};

                    const matchingSubaccount = project.subaccounts.find(subaccount => subaccount.name.toLowerCase() === entry['Subaccount'].toLowerCase());
                    if (!matchingSubaccount) {
                        setResult("Subaccount not found for \"" + entry['Subaccount'] + "\" at row " + i);
                        break;
                    }

                    const parsedAmount = parseAmount(entry['Amount']);

                    if (!parsedAmount.currency) {
                        console.log("Currency not found in amount:", parsedAmount.amount);
                        break;
                    }
                    const matchingCurrency = project.currencies.find(currency =>
                        currency.name.toLowerCase() === parsedAmount.currency.toLowerCase() ||
                        currency.symbol.toLowerCase() === parsedAmount.currency.toLowerCase()
                    );
                    if (!matchingCurrency) {
                        setResult("Currency not found for \"" + parsedAmount.currency + "\" at row " + i);
                        break;
                    }

                    if (entry['Type'].toLowerCase() === 'payment') {
                        const matchingCashAccount = project.cashaccounts.find(account => account.name.toLowerCase() === entry['From'].toLowerCase());
                        const matchingExpenseAccount = project.expenseaccounts.find(expenseaccount => expenseaccount.name.toLowerCase() === entry['To'].toLowerCase());
                        const matchingSupplier = project.suppliers.find(supplier => supplier.name.toLowerCase() === entry['Supplier'].toLowerCase());

                        if (!matchingCashAccount) {
                            setResult("Cash account not found for \"" + entry['From'] + "\" at row " + i);
                            break;
                        }

                        if (!matchingExpenseAccount) {
                            setResult("Expense account not found for \"" + entry['To'] + "\" at row " + i);
                            break;
                        }

                        if (!matchingSupplier) {
                            setResult("Supplier not found for \"" + entry['Supplier'] + "\" at row " + i);
                            break;
                        }

                        convertedEntry = {
                            type: 'payment',
                            name: entry['Name'],
                            from: matchingCashAccount.id,
                            subaccountid: matchingSubaccount.id,
                            to: matchingExpenseAccount.id,
                            ts: Math.floor(new Date(entry['Date']).getTime() / 1000),
                            amount: parsedAmount.amount,
                            currencyid: matchingCurrency.id
                        };
                    }
                    if (entry['Type'].toLowerCase() === 'receipt') {
                        const matchingClient = project.clients.find(client => client.name.toLowerCase() === entry['From'].toLowerCase());
                        const matchingCashAccount = project.cashaccounts.find(account => account.name.toLowerCase() === entry['To'].toLowerCase());

                        if (!matchingClient) {
                            setResult("Client not found for " + entry['From'] + " at row " + (i + 1));
                            break;
                        }

                        if (!matchingCashAccount) {
                            setResult("Cash account not found for \"" + entry['To'] + "\" at row " + (i + 1));
                            break;
                        }

                        convertedEntry = {
                            type: 'receipt',
                            name: entry['Name'],
                            from: matchingClient.id,
                            subaccountid: matchingSubaccount.id,
                            to: matchingCashAccount.id,
                            ts: Math.floor(new Date(entry['Date']).getTime() / 1000),
                            amount: parsedAmount.amount,
                            currencyid: matchingCurrency.id
                        };
                    }

                    convertedEntries.push(convertedEntry);
                }

                if (convertedEntries.length === data.length) {
                    setNewEntries(convertedEntries);
                    setResult(`Data is valid. Entries found: ${convertedEntries.length}`);
                }
            }

        }, [importText]);

        const handleImport = () => {
            console.log('Trying to import data');

            if (newEntries.length == 0) {
                setResult('No new entries to import');
                return;
            }

            setLoading(true);
            setResult(null);

            const data = {
                projectuserid: project.projectuserid,
                projectid: project.projectid,
                transactions: newEntries
            }

            axios.post(configData.CONTROLLERURL + configData.IMPORTTRANSACTIONS, data, {
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                    "userid": state.user.userid,
                    "usertoken": state.user.usertoken
                }
            }).then((res) => {
                console.log('Import data received')
                console.log(res.data)
                if (res.data instanceof Object) {
                    if (res.data.code === 1) {
                        dispatch({
                            type: 'IMPORT_TRANSACTIONS',
                            transactions: newEntries
                        });
                        handleCloseModal();
                    }
                    else {
                        setResult(res.data.data);
                    }
                }
                else {
                    setResult('Error');
                }
            }).catch((err) => {
                setResult(err.response?.data?.message || err.message || 'Network error');
            }).finally(() => {
                setLoading(false);
            });

            setResult('Import successful');
        };

        return (
            <div className='modal_body'>
                <div className='modal_printable'>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <textarea
                                className='modal_textarea'
                                value={importText}
                                onChange={e => setImportText(e.target.value)}
                                placeholder="Paste your import data here..."
                                rows={10}
                                cols={50}
                                style={{ resize: 'vertical' }}
                            />
                        </div>
                    </div>
                </div>
                <div className='modal_actions'>
                    {result && <div className='modal_result'>{result}</div>}
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleImport} disabled={loading}>{loading ? 'Loading...' : 'Import'}</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const renderAccountOptions = (accountType, input, output) => {
        switch (accountType) {
            case 'clients':
                return <Select data={project.clients} itemid={input} setItemid={output} />;
            case 'staff':
                return <Select data={project.staff} itemid={input} setItemid={output} />;
            case 'assetaccounts':
                return <Select data={project.assetaccounts} itemid={input} setItemid={output} />;
            case 'suppliers':
                return <Select data={project.suppliers} itemid={input} setItemid={output} />;
            case 'liabilityaccounts':
                return <Select data={project.liabilityaccounts} itemid={input} setItemid={output} />;
            case 'equityaccounts':
                return <Select data={project.equityaccounts} itemid={input} setItemid={output} />;
            case 'incomeaccounts':
                return <Select data={project.incomeaccounts} itemid={input} setItemid={output} />;
            case 'expenseaccounts':
                return <Select data={project.expenseaccounts} itemid={input} setItemid={output} />;
            default:
                return null;
        }
    };

    function renderDetails(from, to) {
        if (from === 'cashaccounts') {
            switch (to) {
                case 'clients':
                    return 'Refund to client';
                case 'suppliers':
                    return 'Pay supplier (Accrual basis)';
                case 'staff':
                    return 'Pay staff salary or wages (Accrual basis)';
                case 'assetaccounts':
                    return 'Purchase asset';
                case 'expenseaccounts':
                    return 'Pay for expense (Cash basis)';
                case 'liabilityaccounts':
                    return 'Pay for supplier (Accrual basis)';
                default:
                    return '';
            }
        }
        else if (to === 'cashaccounts') {
            switch (from) {
                case 'clients':
                    return 'Receive payment from client';
                case 'suppliers':
                    return 'Refund from supplier';
                case 'staff':
                    return 'Salary refund or repayment';
                case 'assetaccounts':
                    return 'Sell asset';
                case 'liabilityaccounts':
                    return 'Settlement (or payment) of a liability';
                case 'equityaccounts':
                    return 'Invests money into the business';
                default:
                    return '';
            }
        }
    }

    function AddReceipt() {
        const [selecteddate, setSelecteddate] = useState(new Date());
        const [timestamp, setTimestamp] = useState(0);

        const [name, setName] = useState('');

        const [fromtype, setFromtype] = useState('clients');
        const [from, setFrom] = useState('');
        const [totype, setTotype] = useState('cashaccounts');
        const [to, setTo] = useState('');

        const [subaccountid, setSubaccountid] = useState(project?.subaccounts[0]?.id);

        const [amount, setAmount] = useState(0);
        const [currencyid, setCurrencyid] = useState(project?.basecurrency);

        const [result, setResult] = useState(null);
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            const dateAtMidday = new Date(selecteddate);
            dateAtMidday.setHours(12, 0, 0, 0);
            const unixTimestamp = Math.floor(dateAtMidday.getTime() / 1000);
            setTimestamp(unixTimestamp);
        }, [selecteddate]);

        const addReceipt = () => {
            console.log('Trying to add transaction');

            setResult('');

            const hasPermission = getPermission(
                project.projectuserid,
                project.users,
                state.user.userid,
                'Transactions',
                'add',
                project.archived
            );
            if (hasPermission.code == 0) {
                setResult(hasPermission.data);
                return;
            }

            if (project.transactions.length > 1000) {
                setResult('Exceeded limit, please use the POS module.');
                return;
            }

            if (!name || !subaccountid || !currencyid || amount === 0) {
                setResult('Missing info');
                return;
            }

            setLoading(true);

            const trimmedName = name.trim();

            const data = {
                projectuserid: project.projectuserid,
                projectid: project.projectid,
                ts: timestamp,
                name: trimmedName,
                fromtype: fromtype,
                from: from,
                totype: totype,
                to: to,
                subaccountid: subaccountid,
                amount: amount,
                currencyid: currencyid,
                type: 'receipt'
            }

            axios.post(configData.CONTROLLERURL + configData.ADDTRANSACTION, data, {
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                    "userid": state.user.userid,
                    "usertoken": state.user.usertoken
                }
            }).then((res) => {
                console.log('Add transaction data received')
                console.log(res.data)
                if (res.data instanceof Object) {
                    if (res.data.code === 1) {
                        data.id = res.data.id;
                        dispatch({
                            type: 'ADD_TRANSACTION',
                            transaction: data
                        });
                        handleCloseModal();
                    }
                    else {
                        setResult(res.data.data)
                    }
                }
                else {
                    setResult('Error');
                }
            }).catch((err) => {
                setResult(err.response?.data?.message || err.message || 'Network error');
            }).finally(() => {
                setLoading(false);
                setSearch('')
            });
        }

        return (
            <div className='modal_body'>
                <div className='modal_printable'>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Date</div>
                        </div>
                        <div className='modal_rowsection'>
                            <DatePicker
                                className='modal_datepicker'
                                selected={selecteddate}
                                onChange={(date) => setSelecteddate(date)}
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Description</div>
                        </div>
                        <div className='modal_rowsection'>
                            <input
                                className='modal_input'
                                type="text"
                                placeholder="Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>From</div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={receiptAccountTypes} itemid={fromtype} setItemid={setFromtype} />
                        </div>
                        <div className='modal_rowsection'>
                            {renderAccountOptions(fromtype, from, setFrom)}
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>To</div>
                        </div>
                        <div className='modal_rowsection'>
                            Cash Account
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.cashaccounts} itemid={to} setItemid={setTo} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                        </div>
                        <div className='modal_rowsection'>
                            {renderDetails(fromtype, totype)}
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Subaccount</div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.subaccounts} itemid={subaccountid} setItemid={setSubaccountid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Amount</div>
                        </div>
                        <div className='modal_rowsection'>
                            <input
                                className='modal_input'
                                type="number"
                                placeholder="Amount"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Currency</div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.currencies} itemid={currencyid} setItemid={setCurrencyid} />
                        </div>
                    </div>
                </div>
                <div className='modal_actions'>
                    {result && <div className='modal_result'>{result}</div>}
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={() => addReceipt()} disabled={loading}>{loading ? 'Loading...' : 'Save'}</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    function AddPayment() {
        const [selecteddate, setSelecteddate] = useState(new Date());
        const [timestamp, setTimestamp] = useState(0);

        const [name, setName] = useState('');

        const [fromtype, setFromtype] = useState('cashaccounts');
        const [from, setFrom] = useState('');
        const [totype, setTotype] = useState('expenseaccounts');
        const [to, setTo] = useState('');
        const [beneficiarytype, setBeneficiarytype] = useState('');
        const [beneficiaryid, setBeneficiaryid] = useState('');

        const [subaccountid, setSubaccountid] = useState(project?.subaccounts[0]?.id);

        const [amount, setAmount] = useState(0);
        const [currencyid, setCurrencyid] = useState(project?.basecurrency);

        const [result, setResult] = useState(null);
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            let basis = '';
            let type = '';
            let account = '';
            if (beneficiarytype == 'staff') {
                let staff = project.staff.find(staffmember => staffmember.id == beneficiaryid)
                basis = staff?.basis;
                type = 'liabilityaccounts'
                account = staff?.liabilityaccountid
            }
            if (beneficiarytype == 'suppliers') {
                let supplier = project.suppliers.find(supplier => supplier.id == beneficiaryid)
                basis = supplier?.basis;
                type = 'liabilityaccounts'
                account = supplier?.liabilityaccountid
            }
            if (beneficiarytype == 'clients') {
                let client = project.clients.find(client => client.id == beneficiaryid)
                basis = client?.basis;
                type = 'assetaccounts'
                account = client?.assetaccountid
            }
            setTotype(type);
            setTo(account)
        }, [beneficiarytype, beneficiaryid]);

        useEffect(() => {
            const dateAtMidday = new Date(selecteddate);
            dateAtMidday.setHours(12, 0, 0, 0);
            const unixTimestamp = Math.floor(dateAtMidday.getTime() / 1000);
            setTimestamp(unixTimestamp);
        }, [selecteddate]);

        const addPayment = () => {
            console.log('Trying to add transaction');

            setResult('');

            const hasPermission = getPermission(
                project.projectuserid,
                project.users,
                state.user.userid,
                'Transactions',
                'add',
                project.archived
            );
            if (hasPermission.code == 0) {
                setResult(hasPermission.data);
                return;
            }

            if (project.transactions.length > 1000) {
                setResult('Exceeded limit, please use the POS module.');
                return;
            }

            if (!name || !fromtype || !from || !to || !totype || !beneficiaryid || !subaccountid || amount === 0 || !currencyid) {
                setResult('Missing info');
                return;
            }

            setLoading(true);

            const trimmedName = name.trim();

            const data = {
                projectuserid: project.projectuserid,
                projectid: project.projectid,
                ts: timestamp,
                name: trimmedName,
                fromtype: fromtype,
                from: from,
                totype: totype,
                to: to,
                beneficiarytype: beneficiarytype,
                beneficiaryid: beneficiaryid,
                subaccountid: subaccountid,
                amount: amount,
                currencyid: currencyid,
                type: 'payment'
            }

            axios.post(configData.CONTROLLERURL + configData.ADDTRANSACTION, data, {
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                    "userid": state.user.userid,
                    "usertoken": state.user.usertoken
                }
            }).then((res) => {
                console.log('Add transaction data received')
                console.log(res.data)
                if (res.data instanceof Object) {
                    if (res.data.code === 1) {
                        data.id = res.data.id;
                        dispatch({
                            type: 'ADD_TRANSACTION',
                            transaction: data
                        });
                        handleCloseModal();
                    }
                    else {
                        setResult(res.data.data);
                    }
                }
                else {
                    setResult('Error');
                }
            }).catch((err) => {
                setResult(err.response?.data?.message || err.message || 'Network error');
            }).finally(() => {
                setLoading(false);
                setSearch('')
            });
        }

        return (
            <div className='modal_body'>
                <div className='modal_printable'>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Date</div>
                        </div>
                        <div className='modal_rowsection'>
                            <DatePicker
                                className='modal_datepicker'
                                selected={selecteddate}
                                onChange={(date) => setSelecteddate(date)}
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Description</div>
                        </div>
                        <div className='modal_rowsection'>
                            <input
                                className='modal_input'
                                type="text"
                                placeholder="Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>From</div>
                        </div>
                        <div className='modal_rowsection'>
                            Cash Account
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.cashaccounts} itemid={from} setItemid={setFrom} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>To</div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={paymentAccountTypes} itemid={totype} setItemid={setTotype} />
                        </div>
                        <div className='modal_rowsection'>
                            {renderAccountOptions(totype, to, setTo)}
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Beneficiary</div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={paymentBeneficiaryAccountTypes} itemid={beneficiarytype} setItemid={setBeneficiarytype} />
                        </div>
                        <div className='modal_rowsection'>
                            {renderAccountOptions(beneficiarytype, beneficiaryid, setBeneficiaryid)}
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                        </div>
                        <div className='modal_rowsection'>
                            {renderDetails(fromtype, totype)}
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Subaccount</div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.subaccounts} itemid={subaccountid} setItemid={setSubaccountid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Amount</div>
                        </div>
                        <div className='modal_rowsection'>
                            <input
                                className='modal_input'
                                type="number"
                                placeholder="Amount"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Currency</div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.currencies} itemid={currencyid} setItemid={setCurrencyid} />
                        </div>
                    </div>
                </div>
                <div className='modal_actions'>
                    {result && <div className='modal_result'>{result}</div>}
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={() => addPayment()} disabled={loading}>{loading ? 'Loading...' : 'Save'}</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div >
        )
    }

    function EditReceipt({ transaction }) {
        const [id, setId] = useState('');

        const [selecteddate, setSelecteddate] = useState(new Date());
        const [ts, setTs] = useState(0);

        const [name, setName] = useState('');

        const [fromtype, setFromtype] = useState('');
        const [from, setFrom] = useState('');
        const [totype, setTotype] = useState('cashaccounts');
        const [to, setTo] = useState('');

        const [subaccountid, setSubaccountid] = useState('');

        const [amount, setAmount] = useState(0);
        const [currencyid, setCurrencyid] = useState('');

        const [result, setResult] = useState(null);
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            const dateAtMidday = new Date(selecteddate);
            dateAtMidday.setHours(12, 0, 0, 0);
            const unixTimestamp = Math.floor(dateAtMidday.getTime() / 1000);
            setTs(unixTimestamp);
        }, [selecteddate]);

        useEffect(() => {
            setId(transaction.id);
            setSelecteddate(new Date(transaction.ts * 1000));
            setName(transaction.name);
            setFromtype(transaction.fromtype);
            setFrom(transaction.from);
            setTo(transaction.to);
            setSubaccountid(transaction.subaccountid);
            setAmount(transaction.amount);
            setCurrencyid(transaction.currencyid);
        }, [transaction]);

        const updateReceipt = () => {
            console.log('Trying to update transaction');

            setResult('');

            const hasPermission = getPermission(
                project.projectuserid,
                project.users,
                state.user.userid,
                'Transactions',
                'update',
                project.archived
            );
            if (hasPermission.code == 0) {
                setResult(hasPermission.data);
                return;
            }

            if (!name || !currencyid || amount === 0) {
                setResult('Missing info');
                return;
            }

            setLoading(true);

            const trimmedName = name.trim();

            const data = {
                projectuserid: project.projectuserid,
                projectid: project.projectid,
                id: id,
                ts: ts,
                name: trimmedName,
                fromtype: fromtype,
                from: from,
                totype: totype,
                to: to,
                subaccountid: subaccountid,
                amount: amount,
                currencyid: currencyid,
                type: 'receipt'
            }

            axios.post(configData.CONTROLLERURL + configData.UPDATETRANSACTION, data, {
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                    "userid": state.user.userid,
                    "usertoken": state.user.usertoken
                }
            }).then((res) => {
                console.log('Update transaction data received')
                console.log(res.data)
                if (res.data instanceof Object) {
                    if (res.data.code === 1) {
                        dispatch({
                            type: 'UPDATE_TRANSACTION',
                            transaction: data
                        });
                        handleCloseModal();
                    }
                    else {
                        setResult(res.data.data);
                    }
                }
                else {
                    setResult('Error');
                }
            }).catch((err) => {
                setResult(err.response?.data?.message || err.message || 'Network error');
            }).finally(() => {
                setLoading(false);
                setSearch('')
            });
        }

        return (
            <div className='modal_body'>
                <div className='modal_printable'>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Date</div>
                        </div>
                        <div className='modal_rowsection'>
                            <DatePicker
                                className='modal_datepicker'
                                selected={selecteddate}
                                onChange={(date) => setSelecteddate(date)}
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Description</div>
                        </div>
                        <div className='modal_rowsection'>
                            <input
                                className='modal_input'
                                type="text"
                                placeholder="Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>From</div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={receiptAccountTypes} itemid={fromtype} setItemid={setFromtype} />
                        </div>
                        <div className='modal_rowsection'>
                            {renderAccountOptions(fromtype, from, setFrom)}
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>To</div>
                        </div>
                        <div className='modal_rowsection'>
                            Cash Account
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.cashaccounts} itemid={to} setItemid={setTo} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                        </div>
                        <div className='modal_rowsection'>
                            {renderDetails(fromtype, totype)}
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Subaccount</div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.subaccounts} itemid={subaccountid} setItemid={setSubaccountid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Amount</div>
                        </div>
                        <div className='modal_rowsection'>
                            <input
                                className='modal_input'
                                type="number"
                                placeholder="Amount"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Currency</div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.currencies} itemid={currencyid} setItemid={setCurrencyid} />
                        </div>
                    </div>
                </div>
                <div className='modal_actions'>
                    {result && <div className='modal_result'>{result}</div>}
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={() => updateReceipt()} disabled={loading}>{loading ? 'Loading...' : 'Update'}</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={() => removeTransactionButton(transaction)}>Remove</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    function EditPayment({ transaction }) {
        const [id, setId] = useState('');

        const [selecteddate, setSelecteddate] = useState(new Date());
        const [ts, setTs] = useState(0);

        const [name, setName] = useState('');

        const [fromtype, setFromtype] = useState('cashaccounts');
        const [from, setFrom] = useState('');
        const [totype, setTotype] = useState('');
        const [to, setTo] = useState('');
        const [beneficiarytype, setBeneficiarytype] = useState('');
        const [beneficiaryid, setBeneficiaryid] = useState('');

        const [subaccountid, setSubaccountid] = useState('');

        const [amount, setAmount] = useState(0);
        const [currencyid, setCurrencyid] = useState('');

        const [result, setResult] = useState(null);
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            const dateAtMidday = new Date(selecteddate);
            dateAtMidday.setHours(12, 0, 0, 0);
            const unixTimestamp = Math.floor(dateAtMidday.getTime() / 1000);
            setTs(unixTimestamp);
        }, [selecteddate]);

        useEffect(() => {
            setId(transaction.id);
            setSelecteddate(new Date(transaction.ts * 1000));
            setName(transaction.name);
            setFrom(transaction.from)
            setTotype(transaction.totype);
            setTo(transaction.to);
            setBeneficiarytype(transaction.beneficiarytype);
            setBeneficiaryid(transaction.beneficiaryid);
            setTo(transaction.to);
            setSubaccountid(transaction.subaccountid);
            setAmount(transaction.amount);
            setCurrencyid(transaction.currencyid);
        }, [transaction]);

        const updatePayment = () => {
            console.log('Trying to update transaction');

            setResult('');

            const hasPermission = getPermission(
                project.projectuserid,
                project.users,
                state.user.userid,
                'Transactions',
                'update',
                project.archived
            );
            if (hasPermission.code == 0) {
                setResult(hasPermission.data);
                return;
            }

            if (!name || !fromtype || !from || !totype || !to || !beneficiaryid || !subaccountid || amount === 0 || !currencyid) {
                setResult('Missing info');
                return;
            }

            setLoading(true);

            const trimmedName = name.trim();

            const data = {
                projectuserid: project.projectuserid,
                projectid: project.projectid,
                id: id,
                ts: ts,
                name: trimmedName,
                fromtype: fromtype,
                from: from,
                totype: totype,
                to: to,
                beneficiarytype: beneficiarytype,
                beneficiaryid: beneficiaryid,
                subaccountid: subaccountid,
                amount: amount,
                currencyid: currencyid,
                type: 'payment'
            }

            axios.post(configData.CONTROLLERURL + configData.UPDATETRANSACTION, data, {
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                    "userid": state.user.userid,
                    "usertoken": state.user.usertoken
                }
            }).then((res) => {
                console.log('Update transaction data received')
                console.log(res.data)
                if (res.data instanceof Object) {
                    if (res.data.code === 1) {
                        dispatch({
                            type: 'UPDATE_TRANSACTION',
                            transaction: data
                        });
                        handleCloseModal();
                    }
                    else {
                        setResult(res.data.data);
                    }
                }
                else {
                    setResult('Error');
                }
            }).catch((err) => {
                setResult(err.response?.data?.message || err.message || 'Network error');
            }).finally(() => {
                setLoading(false);
                setSearch('')
            });
        }

        return (
            <div className='modal_body'>
                <div className='modal_printable'>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Date</div>
                        </div>
                        <div className='modal_rowsection'>
                            <DatePicker
                                className='modal_datepicker'
                                selected={selecteddate}
                                onChange={(date) => setSelecteddate(date)}
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Description</div>
                        </div>
                        <div className='modal_rowsection'>
                            <input
                                className='modal_input'
                                type="text"
                                placeholder="Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>From</div>
                        </div>
                        <div className='modal_rowsection'>
                            Cash Account
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.cashaccounts} itemid={from} setItemid={setFrom} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>To</div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={paymentAccountTypes} itemid={totype} setItemid={setTotype} />
                        </div>
                        <div className='modal_rowsection'>
                            {renderAccountOptions(totype, to, setTo)}
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Beneficiary</div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={paymentBeneficiaryAccountTypes} itemid={beneficiarytype} setItemid={setBeneficiarytype} />
                        </div>
                        <div className='modal_rowsection'>
                            {renderAccountOptions(beneficiarytype, beneficiaryid, setBeneficiaryid)}
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                        </div>
                        <div className='modal_rowsection'>
                            {renderDetails(fromtype, totype)}
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Subaccount</div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.subaccounts} itemid={subaccountid} setItemid={setSubaccountid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Amount</div>
                        </div>
                        <div className='modal_rowsection'>
                            <input
                                className='modal_input'
                                type="number"
                                placeholder="Amount"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Currency</div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.currencies} itemid={currencyid} setItemid={setCurrencyid} />
                        </div>
                    </div>
                </div>
                <div className='modal_actions'>
                    {result && <div className='modal_result'>{result}</div>}
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={() => updatePayment()} disabled={loading}>{loading ? 'Loading...' : 'Update'}</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={() => removeTransactionButton(transaction)}>Remove</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const ViewReceipt = ({ transaction }) => {
        const [copy, setCopy] = useState(0);
        const printRef = useRef();

        useEffect(() => {
            setCopy(2)
        }, [transaction]);

        const handleSavePDF = () => {
            try {
                const clientName = project.clients.find(client => client.id === transaction.from)?.name || 'Client';
                const formattedClientName = clientName.replace(/\s+/g, '_').toLowerCase();
                const currentDate = moment().format('DD_MM_YYYY');

                const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
                registerArabicFont(doc);

                const pageWidth = doc.internal.pageSize.getWidth();
                let currentY = 10;

                doc.setFontSize(20);
                const title = 'Receipt';
                const titleWidth = doc.getTextWidth(title);
                doc.text(title, (pageWidth - titleWidth) / 2, currentY);

                currentY += 10;
                doc.setFontSize(12);
                doc.text('Project: ' + project.projectdetails, 10, currentY);

                currentY += 5;
                doc.text('Receipt ID: ' + transaction.id, 10, currentY);

                currentY += 5;
                doc.text('Received from: ' + clientName, 10, currentY);

                currentY += 5;
                const currencySymbol = project.currencies.find(item => item.id === transaction.currencyid)?.symbol || '';
                doc.text(`Amount: ${currencySymbol} ${parseFloat(transaction.amount).toLocaleString('en-US')}`, 10, currentY);

                currentY += 5;
                doc.text(`The sum of: ${numberToWords(transaction.amount)} ${project.currencies.find(item => item.id === transaction.currencyid)?.name || ''}`, 10, currentY);

                currentY += 5;
                doc.text('For: ' + transaction.name, 10, currentY);

                currentY += 10;
                doc.text('Signature:', 10, currentY);
                currentY += 15;
                doc.line(10, currentY, 60, currentY);

                currentY += 10;
                doc.setFontSize(10);
                doc.text(moment.unix(transaction.ts).format('dddd, Do MMMM YYYY'), (pageWidth - 50) / 2, currentY);

                doc.save(`receipt_${formattedClientName}_${currentDate}.pdf`);
            } catch (error) {
                console.error('Error generating PDF:', error);
            } finally {
            }
        };

        const handleSave = () => {
            const client = project.clients.find(c => c.id === transaction.from);
            const formattedClientName = client.name.replace(/\s+/g, '_').toLowerCase();
            const currency = project.currencies.find(c => c.id === transaction.currencyid) || { symbol: '', name: '' };

            saveButton({
                templates: project.templates?.filter(template => template.type == 'transactionsreceipt'),
                types: ['pdf', 'png', 'txt'],
                context: {
                    contactname: client.name,
                    projectname: project.projectdetails || project.name,
                    id: transaction.id,
                    description: transaction.name,
                    currencysymbol: currency.symbol,
                    currencyname: currency.name,
                    amount: parseFloat(transaction.amount).toLocaleString('en-US'),
                    amountwords: numberToWords(transaction.amount),
                    date: moment.unix(transaction.ts).format('dddd, Do MMMM YYYY'),
                    filename: "receipt" + "_" + formattedClientName + "_" + moment().format('DD_MM_YYYY')
                }
            });
        };

        const handleSend = () => {
            const client = project.clients.find(c => c.id === transaction.from);
            const currency = project.currencies.find(c => c.id === transaction.currencyid) || { symbol: '', name: '' };

            sendButton({
                templates: project.templates?.filter(template => template.type == 'transactionsreceipt'),
                context: {
                    projectname: project.projectdetails || project.name,
                    id: transaction.id,
                    description: transaction.name,
                    currencysymbol: currency.symbol,
                    currencyname: currency.name,
                    amount: parseFloat(transaction.amount).toLocaleString('en-US'),
                    amountwords: numberToWords(transaction.amount),
                    date: moment.unix(transaction.ts).format('dddd, Do MMMM YYYY'),
                }
            });
        };

        return (
            <div className='modal_body'>
                <div className='modal_row modal_center'>
                    <div className='modal_rowsection'>
                        <select className='modal_select' value={copy} onChange={(e) => setCopy(e.target.value)}>
                            <option value={2}>2 copy</option>
                            <option value={3}>3 copy</option>
                        </select>
                    </div>
                </div>
                <div className='modal_printable' ref={printRef}>
                    <div className='transactions_receipt'>
                        <div className='modal_row modal_center'>
                            <div className='modal_rowsection'>
                                {project.projectdetailsimage && (
                                    <img
                                        className="modal_projectdetailsimage"
                                        src={project.projectdetailsimage}
                                        alt="Project Details Image"
                                    />
                                )}
                            </div>
                            <div className='modal_rowsection'>
                                <div className='modal_printabletitle'>RECEIPT</div>
                            </div>
                            <div className='modal_rowsection'>
                                {project.projectdetails}
                            </div>
                        </div>
                        <div className='modal_row'>
                            <div className='modal_rowsection'>
                                <b>Receipt ID</b> {transaction.id}
                            </div>
                        </div>
                        <div className='modal_row'>
                            <div className='modal_rowsection'>
                                <b>Received from</b> {project.clients.find(item => item.id === transaction.from)?.name || 'Client not found'}
                            </div>
                        </div>
                        <div className='modal_row'>
                            <div className='modal_rowsection'>
                                <b>Amount</b> --{project.currencies.find(item => item.id === transaction.currencyid)?.symbol || ''}{parseFloat(transaction.amount).toLocaleString('en-US')}--
                            </div>
                        </div>
                        <div className='modal_row'>
                            <div className='modal_rowsection'>
                                <b>The sum of</b> {numberToWords(transaction.amount)} {project.currencies.find(item => item.id === transaction.currencyid)?.name || ''}
                            </div>
                        </div>
                        <div className='modal_row'>
                            <div className='modal_rowsection'>
                                <b>For</b> {transaction.name}
                            </div>
                        </div>
                        <div className='modal_row'>
                            <div className='modal_rowsection'></div>
                            <div className='modal_rowsection'>
                                <div className='transactions_signature'></div>
                            </div>
                        </div>
                        <div className='modal_row'>
                            <div className='modal_rowsection'></div>
                            <div className='modal_rowsection'>
                                <div className='modal_center'>{moment.unix(transaction.ts).format('dddd, Do MMMM YYYY')}</div>
                            </div>
                        </div>
                    </div>

                    <div className='transactions_separator'></div>

                    <div className='transactions_receipt'>
                        <div className='modal_row modal_center'>
                            <div className='modal_rowsection'>
                                {project.projectdetailsimage && (
                                    <img
                                        className="modal_projectdetailsimage"
                                        src={project.projectdetailsimage}
                                        alt="Project Details Image"
                                    />
                                )}
                            </div>
                            <div className='modal_rowsection'>
                                <div className='modal_printabletitle'>RECEIPT</div>
                            </div>
                            <div className='modal_rowsection'>
                                {project.projectdetails}
                            </div>
                        </div>
                        <div className='modal_row'>
                            <div className='modal_rowsection'>
                                <b>Receipt ID</b> {transaction.id}
                            </div>
                        </div>
                        <div className='modal_row'>
                            <div className='modal_rowsection'>
                                <b>Received from</b> {project.clients.find(item => item.id === transaction.from)?.name || 'Client not found'}
                            </div>
                        </div>
                        <div className='modal_row'>
                            <div className='modal_rowsection'>
                                <b>Amount</b> --{project.currencies.find(item => item.id === transaction.currencyid)?.symbol || ''}{parseFloat(transaction.amount).toLocaleString('en-US')}--
                            </div>
                        </div>
                        <div className='modal_row'>
                            <div className='modal_rowsection'>
                                <b>The sum of</b> {numberToWords(transaction.amount)} {project.currencies.find(item => item.id === transaction.currencyid)?.name || ''}
                            </div>
                        </div>
                        <div className='modal_row'>
                            <div className='modal_rowsection'>
                                <b>For</b> {transaction.name}
                            </div>
                        </div>
                        <div className='modal_row'>
                            <div className='modal_rowsection'></div>
                            <div className='modal_rowsection'>
                                <div className='transactions_signature'></div>
                            </div>
                        </div>
                        <div className='modal_row'>
                            <div className='modal_rowsection'></div>
                            <div className='modal_rowsection'>
                                <div className='modal_center'>{moment.unix(transaction.ts).format('dddd, Do MMMM YYYY')}</div>
                            </div>
                        </div>
                    </div>

                    <div className='transactions_separator'></div>

                    {
                        copy == 3 &&
                        <div className='transactions_receipt'>
                            <div className='modal_row modal_center'>
                                <div className='modal_rowsection'>

                                </div>
                                <div className='modal_rowsection'>
                                    <div className='modal_printabletitle'>RECEIPT</div>
                                </div>
                                <div className='modal_rowsection'>
                                    {project.projectdetails}
                                </div>
                            </div>
                            <div className='modal_row'>
                                <div className='modal_rowsection'>
                                    <b>Receipt ID</b> {transaction.id}
                                </div>
                            </div>
                            <div className='modal_row'>
                                <div className='modal_rowsection'>
                                    <b>Received from</b> {project.clients.find(item => item.id === transaction.from)?.name || 'Client not found'}
                                </div>
                            </div>
                            <div className='modal_row'>
                                <div className='modal_rowsection'>
                                    <b>Amount</b> --{project.currencies.find(item => item.id === transaction.currencyid)?.symbol || ''}{parseFloat(transaction.amount).toLocaleString('en-US')}--
                                </div>
                            </div>
                            <div className='modal_row'>
                                <div className='modal_rowsection'>
                                    <b>The sum of</b> {numberToWords(transaction.amount)} {project.currencies.find(item => item.id === transaction.currencyid)?.name || ''}
                                </div>
                            </div>
                            <div className='modal_row'>
                                <div className='modal_rowsection'>
                                    <b>For</b> {transaction.name}
                                </div>
                            </div>
                            <div className='modal_row'>
                                <div className='modal_rowsection'></div>
                                <div className='modal_rowsection'>
                                    <div className='transactions_signature'></div>
                                </div>
                            </div>
                            <div className='modal_row'>
                                <div className='modal_rowsection'></div>
                                <div className='modal_rowsection'>
                                    <div className='modal_center'>{moment.unix(transaction.ts).format('dddd, Do MMMM YYYY')}</div>
                                </div>
                            </div>
                        </div>
                    }
                </div>
                <div className='modal_actions'>
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                            <ReactToPrint
                                trigger={() => (<button className="modal_button">Print</button>)}
                                content={() => printRef.current}
                            />
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleSave}>Save</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleSavePDF}>Save PDF</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleSend}>Send</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    function ViewPayment({ transaction }) {
        const [totype, setTotype] = useState('');
        const [to, setTo] = useState('');
        const [beneficiarytype, setBeneficiarytype] = useState('');
        const [beneficiaryid, setBeneficiaryid] = useState('');

        useEffect(() => {
            setTotype(transaction.totype)
            setTo(transaction.to)
            setBeneficiarytype(transaction.beneficiarytype)
            setBeneficiaryid(transaction.beneficiaryid)
        }, [transaction]);

        const printRef = useRef();

        const handleSave = () => {
            const supplier = project.suppliers.find(s => s.id === transaction.beneficiaryid);
            const formattedSupplierName = supplier.name.replace(/\s+/g, '_').toLowerCase();
            const currency = project.currencies.find(c => c.id === transaction.currencyid) || { symbol: '', name: '' };

            saveButton({
                templates: project.templates?.filter(template => template.type == 'transactionspayment'),
                types: ['pdf', 'png', 'txt'],
                context: {
                    beneficiaryname: supplier.name,
                    projectname: project.projectdetails || project.name,
                    id: transaction.id,
                    description: transaction.name,
                    currencysymbol: currency.symbol,
                    currencyname: currency.name,
                    amount: parseFloat(transaction.amount).toLocaleString('en-US'),
                    amountwords: numberToWords(transaction.amount),
                    datetime: moment.unix(transaction.ts).format('dddd, Do MMMM YYYY'),
                    filename: "payment" + "_" + formattedSupplierName + "_" + moment().format('DD_MM_YYYY')
                }
            });
        };

        const handleSend = () => {
            let contact = '';
            if (transaction.beneficiarytype == 'supplier') {
                contact = project.suppliers.find(c => c.id === transaction.beneficiaryid);
            }
            if (transaction.beneficiarytype == 'staff') {
                contact = project.staff.find(c => c.id === transaction.beneficiaryid);
            }
            const currency = project.currencies.find(c => c.id === transaction.currencyid) || { symbol: '', name: '' };

            sendButton({
                templates: project.templates?.filter(template => template.type == 'transactionspayment'),
                contact: contact,
                context: {
                    projectname: project.projectdetails || project.name,
                    id: transaction.id,
                    description: transaction.name,
                    currencysymbol: currency.symbol,
                    currencyname: currency.name,
                    amount: parseFloat(transaction.amount).toLocaleString('en-US'),
                    amountwords: numberToWords(transaction.amount),
                    date: moment.unix(transaction.ts).format('dddd, Do MMMM YYYY'),
                }
            });
        };

        return (
            <div className='modal_body'>
                <div className='modal_printable' ref={printRef}>
                    <div className='modal_row modal_center'>
                        <div className='modal_rowsection'>
                            {project.projectdetailsimage && (
                                <img
                                    className="modal_projectdetailsimage"
                                    src={project.projectdetailsimage}
                                    alt="Project Details Image"
                                />
                            )}
                        </div>
                        <div className='modal_rowsection'>
                            <div className='modal_printabletitle'>PAYMENT VOUCHER</div>
                        </div>
                        <div className='modal_rowsection'>
                            {project.projectdetails}
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <b>Project</b> {project.name}
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <b>Payment ID</b> {transaction.id}
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <b>Amount</b> --{project.currencies.find(item => item.id === transaction.currencyid)?.symbol || ''}{parseFloat(transaction.amount).toLocaleString('en-US')}--
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <b>The sum of</b> {numberToWords(transaction.amount)} {project.currencies.find(item => item.id === transaction.currencyid)?.name || ''}
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <b>Payment to</b> {renderName(totype, to)}
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <b>Beneficiary</b> {renderName(beneficiarytype, beneficiaryid)}
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <b>Description</b> {transaction.name}
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'></div>
                        <div className='modal_rowsection'>
                            <div className='transactions_signature'></div>
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'></div>
                        <div className='modal_rowsection modal_center'>
                            <div className='modal_date'>{moment.unix(transaction.ts).format('dddd, Do MMMM YYYY')}</div>
                        </div>
                    </div>
                </div>
                <div className='modal_actions'>
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                            <ReactToPrint
                                trigger={() => (<button className="modal_button">Print</button>)}
                                content={() => printRef.current}
                            />
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleSave}>Save</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button
                                className="modal_button"
                                onClick={handleSend}
                            >
                                Send
                            </button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    function RemoveTransaction({ transaction }) {
        const [id, setId] = useState('');

        const [result, setResult] = useState(null);
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            if (transaction) {
                setId(transaction.id);
                console.log(transaction)
            }
        }, [transaction]);

        const removeTransaction = (id) => {
            console.log('Trying to remove transaction');

            setResult('');

            const hasPermission = getPermission(
                project.projectuserid,
                project.users,
                state.user.userid,
                'Transactions',
                'remove',
                project.archived
            );
            if (hasPermission.code == 0) {
                setResult(hasPermission.data);
                return;
            }

            const data = {
                projectuserid: project.projectuserid,
                projectid: project.projectid,
                id: id
            }

            setLoading(true);

            axios.post(configData.CONTROLLERURL + configData.REMOVETRANSACTION, data, {
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                    "userid": state.user.userid,
                    "usertoken": state.user.usertoken
                }
            }).then((res) => {
                console.log('Remove transaction data received')
                console.log(res.data)
                if (res.data instanceof Object) {
                    if (res.data.code === 1) {
                        dispatch({
                            type: 'REMOVE_TRANSACTION',
                            id: id
                        });
                        handleCloseModal();
                    }
                    else {
                        setResult(res.data.data);
                    }
                }
                else {
                    setResult('Error');
                }
            }).catch((err) => {
                setResult(err.response?.data?.message || err.message || 'Network error');
            }).finally(() => {
                setLoading(false);
                setSearch('')
            });
        }

        const removeSelectedTransactions = () => {
            selectedtransactions.forEach(transaction => {
                removeTransaction(transaction.id);
            });
        };

        return (
            <div className='modal_body'>
                <div className='modal_printable'>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            {selectedtransactions.length > 0 && "Remove " + selectedtransactions.length + " transactions ?"}
                        </div>
                    </div>
                </div>
                <div className='modal_actions'>
                    {result && <div className='modal_result'>{result}</div>}
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                            {transaction && (<button className="modal_button" onClick={() => removeTransaction(id)} disabled={loading}>{loading ? 'Loading...' : 'Remove'}</button>)}
                            {selectedtransactions.length > 0 && (<button className="modal_button" onClick={() => removeSelectedTransactions()} disabled={loading}>{loading ? 'Loading...' : 'Remove Selected'}</button>)}
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    function ViewTransactions({ transactions }) {
        const printRef = useRef();

        return (
            <div className='modal_body'>
                <div className='modal_printable' ref={printRef}>
                    <table className='modal_table'>
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Type</th>
                                <th>ID</th>
                                <th>From</th>
                                <th>To</th>
                                <th>Name</th>
                                <th>Amount</th>
                                <th>Subaccount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                transactions.sort((a, b) => b.ts - a.ts).map((transaction, index) => {
                                    if (transaction.type === 'receipt') {
                                        return (
                                            <tr key={'transaction' + index} className='transactions_receipt'>
                                                <td><div>{moment.unix(transaction.ts).format('D/MMM/YYYY')}</div></td>
                                                <td><div>{transaction.type}</div></td>
                                                <td><div>{transaction.id}</div></td>
                                                <td>
                                                    <div>{renderName(transaction.fromtype, transaction.from)}</div>
                                                </td>
                                                <td>
                                                    <div>{renderName(transaction.totype, transaction.to)}</div>
                                                </td>
                                                <td><div>{transaction.name}</div></td>
                                                <td><div>{project.currencies.find(item => item.id === transaction.currencyid, 10)?.symbol || 'Currency not found'} {parseFloat(transaction.amount).toLocaleString('en-US')}</div></td>
                                                <td>{project.subaccounts.find(subac => subac.id === transaction.subaccountid)?.name}</td>
                                            </tr>
                                        )
                                    }
                                    if (transaction.type === 'payment') {
                                        return (
                                            <tr key={'transaction' + index} className='transactions_payment'>
                                                <td><div>{moment.unix(transaction.ts).format('D/MMM/YYYY')}</div></td>
                                                <td><div>{transaction.type}</div></td>
                                                <td><div>{transaction.id}</div></td>
                                                <td>
                                                    <div>{renderName(transaction.fromtype, transaction.from)}</div>
                                                </td>
                                                <td>
                                                    <div>{renderName(transaction.totype, transaction.to)}</div>
                                                </td>
                                                <td><div>{transaction.name}</div></td>
                                                <td><div>{project.currencies.find(item => item.id === transaction.currencyid, 10)?.symbol || 'Currency not found'} {parseFloat(transaction.amount).toLocaleString('en-US')}</div></td>
                                                <td>{project.subaccounts.find(subac => subac.id === transaction.subaccountid)?.name}</td>
                                            </tr>
                                        )
                                    }
                                })
                            }
                        </tbody>
                    </table>
                </div>
                <div className='modal_actions'>
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                            <ReactToPrint
                                trigger={() => (<button className="modal_button">Print</button>)}
                                content={() => printRef.current}
                            />
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const toggleSelection = (transactionId) => {
        setSelectedtransactions((prev) =>
            prev.includes(transactionId)
                ? prev.filter((id) => id !== transactionId)
                : [...prev, transactionId]
        );
    };

    function renderName(type, id) {
        if (!id) return '';

        switch (type) {
            case 'clients':
                return project.clients.find(c => c.id === id)?.name || 'Unknown client';
            case 'cashaccounts':
                return project.cashaccounts.find(c => c.id === id)?.name || 'Unknown account';
            case 'suppliers':
                return project.suppliers.find(s => s.id === id)?.name || 'Unknown supplier';
            case 'liabilityaccounts':
                return project.liabilityaccounts.find(s => s.id === id)?.name || 'Unknown account';
            case 'expenseaccounts':
                return project.expenseaccounts.find(s => s.id === id)?.name || 'Unknown expense';
            case 'assetaccounts':
                return project.assetaccounts.find(s => s.id === id)?.name || 'Unknown asset';
            case 'equityaccounts':
                return project.equityaccounts.find(a => a.id === id)?.name || 'Unknown equity';
            case 'staff':
                return project.staff.find(s => s.id === id)?.name || 'Unknown staff member';
            default:
                return ''
        }
    }

    return (
        <div className='transactions'>
            <Title text='Transactions' />

            <div className='buttonscontainer'>
                <div className='buttonsection'>
                    <button className='button' onClick={addReceiptButton}>Receipt</button>
                </div>
                <div className='buttonsection'>
                    <button className='button' onClick={addPaymentButton}>Payment</button>
                </div>
                <div className='buttonsection'>
                    <button className='button' onClick={() => viewTransactions(project.transactions)}>View Transactions</button>
                </div>
                <div className='buttonsection'>
                    <input
                        className='input'
                        type="text"
                        placeholder="Search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            <div className='buttonscontainer'>
                <div className='buttonsection'>
                    <button className='button' onClick={() => removeTransactionButton()}>Remove</button>
                </div>
                <div className='buttonsection'>
                    <button className='button' onClick={exportPage}>Export</button>
                </div>
                <div className='buttonsection'>
                    <button className='button' onClick={importPage}>Import</button>
                </div>
            </div>

            <div>
                <table className='table'>
                    <thead>
                        <tr>
                            <th>Select</th>
                            <th></th>
                            <th></th>
                            <th>Date</th>
                            <th>Type</th>
                            <th>ID</th>
                            <th>From</th>
                            <th>To</th>
                            <th>Name</th>
                            <th>Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            filteredtransactions.sort((a, b) => b.ts - a.ts).map((transaction, index) => {
                                if (transaction.type === 'receipt') {

                                    return (
                                        <tr key={'transaction' + index} className='transactions_receipt'>
                                            <td>
                                                <div>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedtransactions.includes(transaction.id)}
                                                        onChange={() => toggleSelection(transaction.id)}
                                                    />
                                                </div>
                                            </td>
                                            <td><div className='table_button' onClick={() => editReceiptButton(transaction)}>Edit</div></td>
                                            <td><div className='table_button' onClick={() => viewReceiptButton(transaction)}>View</div></td>
                                            <td><div>{moment.unix(transaction.ts).format('D/MMM/YYYY')}</div></td>
                                            <td><div>{transaction.type}</div></td>
                                            <td><div>{transaction.id}</div></td>
                                            <td>
                                                <div>  {renderName(transaction.fromtype, transaction.from)}</div>
                                            </td>
                                            <td>
                                                <div>  {renderName(transaction.totype, transaction.to)}</div>
                                            </td>
                                            <td><div>{transaction.name}</div></td>
                                            <td><div>{project.currencies.find(item => item.id === transaction.currencyid, 10)?.symbol || 'Currency not found'} {parseFloat(transaction.amount).toLocaleString('en-US')}</div></td>
                                        </tr>
                                    )
                                }
                                if (transaction.type === 'payment') {
                                    return (
                                        <tr key={'transaction' + index} className='transactions_payment'>
                                            <td>
                                                <div>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedtransactions.includes(transaction.id)}
                                                        onChange={() => toggleSelection(transaction.id)}
                                                    />
                                                </div>
                                            </td>
                                            <td><div className='table_button' onClick={() => editPaymentButton(transaction)}>Edit</div></td>
                                            <td><div className='table_button' onClick={() => viewPaymentButton(transaction)}>View</div></td>
                                            <td><div>{moment.unix(transaction.ts).format('D/MMM/YYYY')}</div></td>
                                            <td><div>{transaction.type}</div></td>
                                            <td><div>{transaction.id}</div></td>
                                            <td>
                                                <div>{renderName(transaction.fromtype, transaction.from)}</div>
                                            </td>
                                            <td>
                                                <div>{renderName(transaction.totype, transaction.to)}</div>
                                            </td>
                                            <td><div>{transaction.name}</div></td>
                                            <td><div>{project.currencies.find(item => item.id === transaction.currencyid, 10)?.symbol || 'Currency not found'} {parseFloat(transaction.amount).toLocaleString('en-US')}</div></td>
                                        </tr>
                                    )
                                }
                            })
                        }
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default Transactions;